import { Component } from '@theme/component';

/**
 * @typedef {{
 *   numberItems: number,
 *   bundlePrice: number,
 *   parentVariantId: string,
 *   productTitle: string,
 *   additionalProducts: Array<{ variantId: number, price: number, title: string }>
 * }} BundleData
 *
 * @typedef {{ id: number, price: number, available: boolean, inventoryQuantity: number }} VariantInfo
 *
 * @typedef {Object} BundleBuilderRefs
 * @property {HTMLElement} colorSection
 * @property {HTMLElement} stepProgress
 * @property {HTMLElement[]} qtyDisplays
 * @property {HTMLElement} bundleData
 *
 * @extends {Component<BundleBuilderRefs>}
 */
class BundleBuilder extends Component {
  /** @type {string | null} */
  #selectedSize = null;

  /** @type {Map<string, number>} handle → quantity */
  #quantities = new Map();

  /** @type {BundleData | null} */
  #data = null;

  connectedCallback() {
    super.connectedCallback();
    const script = this.refs.bundleData;
    if (script) {
      this.#data = JSON.parse(script.textContent || '{}');
    }

    const form = this.querySelector('product-form-component form');
    if (form) {
      form.addEventListener('submit', this.#onFormSubmit, { capture: true });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const form = this.querySelector('product-form-component form');
    if (form) {
      form.removeEventListener('submit', this.#onFormSubmit, { capture: true });
    }
  }

  /**
   * Regenerate pack ID just before product-form-component reads FormData.
   */
  #onFormSubmit = () => {
    delete this.dataset.packId;
    this.#syncFormInputs();
  };

  /**
   * @param {Event} event
   */
  handleSizeChange = (event) => {
    const input = /** @type {HTMLInputElement} */ (event.target);
    this.#selectedSize = input.value;
    this.#quantities.clear();

    const rows = this.querySelectorAll('.bundle-builder__color-row');
    let firstVisible = /** @type {HTMLElement | null} */ (null);

    rows.forEach((row, index) => {
      const el = /** @type {HTMLElement} */ (row);
      const variantMap = this.#getVariantMap(el);
      const variantForSize = /** @type {VariantInfo | undefined} */ (variantMap[this.#selectedSize || '']);

      if (variantForSize) {
        el.hidden = false;
        if (variantForSize.available) {
          el.classList.remove('bundle-builder__color-row--soldout');
          el.dataset.activeVariantId = String(variantForSize.id);
          el.dataset.activePrice = String(variantForSize.price);
          el.dataset.activeInventory = String(variantForSize.inventoryQuantity);
          if (!firstVisible) firstVisible = el;
        } else {
          el.classList.add('bundle-builder__color-row--soldout');
          delete el.dataset.activeVariantId;
        }
      } else {
        el.hidden = true;
        el.classList.remove('bundle-builder__color-row--soldout');
        delete el.dataset.activeVariantId;
      }

      el.classList.remove('bundle-builder__color-row--active');
      const display = this.refs.qtyDisplays[index];
      if (display) display.textContent = '0';
    });

    this.refs.colorSection.classList.remove('bundle-builder__colors--inactive');
    this.#syncFormInputs();
    this.#updateProgress();

    if (firstVisible) {
      const btn = firstVisible.querySelector('.bundle-builder__qty-btn:last-of-type');
      if (btn instanceof HTMLElement) btn.focus();
    }
  };

  /**
   * @param {Event} event
   */
  increaseQty = (event) => {
    const target = /** @type {HTMLElement | null} */ (event.target);
    const row = /** @type {HTMLElement | null} */ (target?.closest('.bundle-builder__color-row'));
    if (!row) return;

    const handle = row.dataset.componentHandle || '';
    const current = this.#quantities.get(handle) || 0;
    const totalOthers = this.#getTotal() - current;
    const maxItems = this.#data?.numberItems || 0;
    const inventoryMax = parseInt(row.dataset.activeInventory || '0', 10);
    const max = Math.min(maxItems - totalOthers, inventoryMax);

    if (current < max) {
      this.#quantities.set(handle, current + 1);
      this.#updateRowDisplay(row, handle);
      this.#syncFormInputs();
      this.#updateProgress();
    }
  };

  /**
   * @param {Event} event
   */
  decreaseQty = (event) => {
    const target = /** @type {HTMLElement | null} */ (event.target);
    const row = /** @type {HTMLElement | null} */ (target?.closest('.bundle-builder__color-row'));
    if (!row) return;

    const handle = row.dataset.componentHandle || '';
    const current = this.#quantities.get(handle) || 0;

    if (current > 0) {
      this.#quantities.set(handle, current - 1);
      this.#updateRowDisplay(row, handle);
      this.#syncFormInputs();
      this.#updateProgress();
    }
  };

  /**
   * Reads the variant map from the embedded <script> element inside a color row.
   * @param {HTMLElement} row
   * @returns {Record<string, VariantInfo>}
   */
  #getVariantMap(row) {
    const script = row.querySelector('script.variant-data');
    if (!script) return {};
    return JSON.parse(script.textContent || '{}');
  }

  /**
   * Syncs hidden form inputs inside <product-form-component> to reflect current quantities.
   * Uses items[N][id], items[N][quantity], items[N][properties][key] naming.
   */
  #syncFormInputs() {
    const container = this.querySelector('[ref="formInputs"]');
    if (!container || !this.#data) return;

    container.innerHTML = '';

    const packId = this.dataset.packId || crypto.randomUUID();
    this.dataset.packId = packId;

    const additionalCount = this.#data.additionalProducts.length;
    const totalComponents = String(this.#data.numberItems + additionalCount);

    let totalComponentPrices = 0;
    /** @type {Array<{ id: string, quantity: string, price: number, properties: Record<string, string> }>} */
    const items = [];

    const rows = this.querySelectorAll('.bundle-builder__color-row');
    rows.forEach((row) => {
      const el = /** @type {HTMLElement} */ (row);
      const handle = el.dataset.componentHandle || '';
      const qty = this.#quantities.get(handle) || 0;
      if (qty <= 0 || !el.dataset.activeVariantId) return;

      const price = parseFloat(el.dataset.activePrice || '0');
      totalComponentPrices += price * qty;

      items.push({
        id: el.dataset.activeVariantId,
        quantity: String(qty),
        price,
        properties: {
          '_pack-id': packId,
          '_pack-price': String(this.#data?.bundlePrice || 0),
          '_pack-total-components': totalComponents,
          '_pack-parent-id': this.#data?.parentVariantId || '',
        },
      });
    });

    for (const prod of this.#data.additionalProducts) {
      totalComponentPrices += prod.price;
      items.push({
        id: String(prod.variantId),
        quantity: '1',
        price: prod.price,
        properties: {
          '_pack-id': packId,
          '_pack-price': String(this.#data.bundlePrice),
          '_pack-total-components': totalComponents,
          '_pack-parent-id': this.#data.parentVariantId,
          '_pack-required-item': 'true',
        },
      });
    }

    const percentage = totalComponentPrices > 0
      ? ((totalComponentPrices - this.#data.bundlePrice) / totalComponentPrices) * 100
      : 0;

    items.forEach((item, i) => {
      this.#addHiddenInput(container, `items[${i}][id]`, item.id);
      this.#addHiddenInput(container, `items[${i}][quantity]`, item.quantity);

      item.properties['_pack-percentage'] = String(percentage);

      for (const [key, value] of Object.entries(item.properties)) {
        this.#addHiddenInput(container, `items[${i}][properties][${key}]`, value);
      }
    });
  }

  /**
   * @param {Element} container
   * @param {string} name
   * @param {string} value
   */
  #addHiddenInput(container, name, value) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    container.appendChild(input);
  }

  #getTotal() {
    let total = 0;
    for (const qty of this.#quantities.values()) {
      total += qty;
    }
    return total;
  }

  /**
   * @param {HTMLElement} row
   * @param {string} handle
   */
  #updateRowDisplay(row, handle) {
    const rows = this.querySelectorAll('.bundle-builder__color-row');
    const index = Array.from(rows).indexOf(row);
    const qty = this.#quantities.get(handle) || 0;
    const display = this.refs.qtyDisplays[index];
    if (display) {
      display.textContent = String(qty);
    }
    row.classList.toggle('bundle-builder__color-row--active', qty > 0);
  }

  #updateProgress() {
    const total = this.#getTotal();
    const max = this.#data?.numberItems || 0;
    const ready = total >= max;
    const button = /** @type {HTMLButtonElement | null} */ (this.querySelector('[ref="addToCartButton"]'));

    this.refs.stepProgress.textContent = `${total}/${max}`;

    const progressText = this.querySelector('[ref="progressText"]');
    if (progressText) {
      progressText.textContent = `${total} / ${max}`;
    }

    const buttonLabel = this.querySelector('[ref="buttonLabel"]');
    if (buttonLabel) {
      buttonLabel.textContent = ready
        ? (Theme.translations.add_to_cart || '')
        : (Theme.translations.bundle_your_kit || '');
    }

    if (button) {
      button.disabled = !ready;
    }
  }
}

if (!customElements.get('bundle-builder')) {
  customElements.define('bundle-builder', BundleBuilder);
}
